import type { Vec2, Vec3 } from '../../../../geo/types';
import { octahedronSolid, orientedFace, planPoint, prismSolid, pyramidOverRing, ringPoints } from '../../solids';
import { gableWall, rectWall, roofUv } from '../masonry';
import { bayWindows, lancet, LIGHTS, type NieuweKerkBuild } from './nieuweKerkParts';

/**
 * The nave of five bays, the crossing and the high choir under one steep slate roof, closed at the
 * west by the gable of 1538 between its two octagonal turrets and at the east by the apse. Their
 * clerestory windows stand over the aisle roofs; the crossing has none, the transept runs through it.
 * The walls of before 1452, the choir's to the south and its apse, are of tuff; the rest is brick
 * banded with stone (De Gids 1942, p. 31).
 */
export const TURRET = { segments: 8, cornice: { drop: 0.5, outset: 0.2 }, finialRise: 0.15, finial: 0.25 } as const;

const PHASE = Math.PI / TURRET.segments;

/*
 * The side walls of the nave and of the choir, each with a window to a bay; the transept's walls close
 * the crossing between them. The choir's south wall is of tuff, the others brick.
 */
function buildSideWalls(s: NieuweKerkBuild): void {
  const { vessel: v, naveBays, choirBays, transept: t } = s.plan;
  const wall = (edge: readonly [Vec2, Vec2], styles: NieuweKerkBuild['stone'], bounds: readonly number[]): void => {
    const direction = edge[1][0] > edge[0][0] ? 1 : -1;
    rectWall(s.builder, styles, s.frame, edge, [0, v.eaveY], bayWindows(bounds, edge[0][0], direction, LIGHTS.clerestory));
  };
  wall([[v.westU, v.v0], [t.u0, v.v0]], s.brick, naveBays);
  wall([[t.u1, v.v0], [v.chordU, v.v0]], s.stone, choirBays);
  wall([[v.chordU, v.v1], [t.u1, v.v1]], s.brick, choirBays);
  wall([[t.u0, v.v1], [v.westU, v.v1]], s.brick, naveBays);
}

function buildApseWalls(s: NieuweKerkBuild): void {
  const { apse, vessel: v } = s.plan;
  apse.slice(1).forEach((p, i) => {
    const q = apse[i]!;
    const facet = Math.hypot(p[0] - q[0], p[1] - q[1]);
    const lights = { ...LIGHTS.clerestory, width: Math.min(LIGHTS.clerestory.width, 0.55 * facet) };
    rectWall(s.builder, s.stone, s.frame, [q, p], [0, v.eaveY], [lancet(facet / 2, lights)]);
  });
}

function buildWestGable(s: NieuweKerkBuild): void {
  const v = s.plan.vessel;
  const width = v.v1 - v.v0;
  const openings = [lancet(width / 2, LIGHTS.west), lancet(width / 2, LIGHTS.portal)];
  gableWall(s.builder, s.brick, s.frame, [[v.westU, v.v1], [v.westU, v.v0]], { eaveY: v.eaveY, apexY: v.ridgeY }, openings);
}

/**
 * The two long slopes from the west gable to the chord, each in two beside the crossing, where the
 * transept's slopes take over from the valleys on the crossing's diagonals (nieuweKerkTransept.ts),
 * and a facet from each side of the apse up to the ridge's end, at the centre of the circle the apse's
 * sides stand on, so every facet takes the long slopes' pitch.
 */
function buildRoof(s: NieuweKerkBuild): void {
  const { vessel: v, apse, vc, transept: t } = s.plan;
  const eave = (p: Vec2): Vec3 => planPoint(p[0], v.eaveY, p[1]);
  const ridge = (u: number): Vec3 => planPoint(u, v.ridgeY, vc);
  const centre = ridge((t.u0 + t.u1) / 2);
  const face = (points: readonly Vec3[], outward: Vec3, uv = roofUv(points[0]!, points[1]!, points[points.length - 1]!)): void => {
    orientedFace(s.builder, s.stone.roof, s.frame, points, outward, uv);
  };
  const south = roofUv(eave([v.westU, v.v0]), eave([v.chordU, v.v0]), ridge(v.hipU));
  face([eave([v.westU, v.v0]), eave([t.u0, v.v0]), centre, ridge(v.westU)], [0, 1, 1], south);
  face([eave([t.u1, v.v0]), eave([v.chordU, v.v0]), ridge(v.hipU), centre], [0, 1, 1], south);
  const north = roofUv(eave([v.chordU, v.v1]), eave([v.westU, v.v1]), ridge(v.westU));
  face([eave([v.chordU, v.v1]), eave([t.u1, v.v1]), centre, ridge(v.hipU)], [0, 1, -1], north);
  face([eave([t.u0, v.v1]), eave([v.westU, v.v1]), ridge(v.westU), centre], [0, 1, -1], north);
  apse.slice(1).forEach((p, i) => {
    const q = apse[i]!;
    face([eave(q), eave(p), ridge(v.hipU)], [p[1] - q[1], 1, p[0] - q[0]]);
  });
}

/** An octagonal stair turret: its shaft, a cornice under the spire, the slate spire and a finial. */
function buildTurret(s: NieuweKerkBuild, t: NieuweKerkBuild['plan']['turrets'][number]): void {
  const ring = ringPoints(t.center, t.radius, TURRET.segments, PHASE);
  const cornice = ringPoints(t.center, t.radius + TURRET.cornice.outset, TURRET.segments, PHASE);
  prismSolid(s.builder, s.brick.wall, s.frame, ring, 0, t.bodyY, { top: false, bottom: false });
  prismSolid(s.builder, s.stone.wall, s.frame, cornice, t.bodyY - TURRET.cornice.drop, t.bodyY, { top: true, bottom: true });
  pyramidOverRing(s.builder, { roof: s.stone.roof, wall: s.stone.roof }, s.frame, ring, t.bodyY, { plan: t.center, y: t.tipY });
  octahedronSolid(s.builder, s.stone.wall, s.frame, planPoint(t.center[0], t.tipY + TURRET.finialRise, t.center[1]), TURRET.finial);
}

export function buildVessel(s: NieuweKerkBuild): void {
  buildSideWalls(s);
  buildApseWalls(s);
  buildWestGable(s);
  buildRoof(s);
  for (const t of s.plan.turrets) buildTurret(s, t);
}
